import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../auth/AuthContext';
import {
  callableErrorMessage,
  displayNameError,
  normalizeDisplayName,
  saveDisplayName
} from '../services/displayName';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(5, 0, 10, 0.7);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 24px;
`;

const Card = styled.div`
  width: min(420px, 100%);
  background: rgba(20, 8, 36, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 18px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  padding: 28px;
  color: #fff;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Label = styled.label`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.75);
`;

const Input = styled.input`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #fff;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 0.95rem;
  font-family: inherit;
  outline: none;

  &:focus {
    border-color: rgba(0, 245, 255, 0.6);
    box-shadow: 0 0 0 3px rgba(0, 245, 255, 0.15);
  }
`;

const Note = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.82rem;
  line-height: 1.4;
`;

const ErrorText = styled.p`
  margin: 0;
  color: #ff8aa8;
  font-size: 0.85rem;
`;

const SavedText = styled.p`
  margin: 0;
  color: #7dffa8;
  font-size: 0.85rem;
`;

const SaveButton = styled.button`
  background: linear-gradient(135deg, #ff00aa 0%, #00f5ff 100%);
  border: none;
  color: #fff;
  border-radius: 999px;
  padding: 12px 16px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

function AccountPanel({ onClose }) {
  const { user, refreshProfile } = useAuth();
  const [name, setName] = useState(() => user?.displayName || '');
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onSubmit = async (event) => {
    event.preventDefault();
    const next = normalizeDisplayName(name);
    const problem = displayNameError(name, user?.uid);
    if (problem) {
      setSaved(false);
      setError(problem);
      return;
    }
    if (next === normalizeDisplayName(user?.displayName || '')) {
      setError('');
      setSaved(true);
      setName(next);
      return;
    }
    setBusy(true);
    setError('');
    setSaved(false);
    try {
      await saveDisplayName(next);
      await refreshProfile();
      setName(next);
      setSaved(true);
    } catch (err) {
      setError(callableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Card onClick={(event) => event.stopPropagation()} role="dialog" aria-labelledby="account-title">
        <Header>
          <Title id="account-title">Account</Title>
          <CloseButton type="button" onClick={onClose} aria-label="Close">
            ×
          </CloseButton>
        </Header>
        <Form onSubmit={onSubmit}>
          <Label htmlFor="display-name">Display name</Label>
          <Input
            id="display-name"
            value={name}
            autoComplete="nickname"
            onChange={(event) => {
              setName(event.target.value);
              setSaved(false);
            }}
          />
          <Note>Your display name appears on any written reviews you post.</Note>
          {error ? <ErrorText>{error}</ErrorText> : null}
          {saved && !error ? <SavedText>Saved.</SavedText> : null}
          <SaveButton type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </SaveButton>
        </Form>
      </Card>
    </Overlay>
  );
}

export default AccountPanel;
